import React, { useEffect } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import HistoryIcon from "@material-ui/icons/History";
import { useQuery } from "react-query";
import { useHistory } from "react-router-dom";

import toastError from "../../errors/toastError";
import api from "../../services/api";

const TWO_MINUTES = 2 * 60 * 1000;

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    flexShrink: 0
  },
  heading: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5)
  },
  headingText: {
    flex: 1
  },
  card: {
    width: "100%",
    padding: theme.spacing(0.75, 1),
    marginTop: theme.spacing(0.75),
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  },
  content: {
    minWidth: 0,
    flex: 1
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    flexWrap: "wrap"
  },
  ellipsis: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  empty: {
    padding: theme.spacing(1.5, 0),
    color: theme.palette.text.secondary
  }
}));

const formatDate = value => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short"
  }).format(date);
};

const ContactOsHistory = ({ clientId, enabled }) => {
  const classes = useStyles();
  const history = useHistory();
  const companyId = localStorage.getItem("companyId") || "default";
  const { data: orders = [], isLoading, error } = useQuery(
    ["contact-os-history", companyId, String(clientId || "")],
    async () => {
      const { data } = await api.get("/os/ordens-servico", {
        params: { cliente_id: clientId, per_page: 10 }
      });
      return data.data || [];
    },
    {
      enabled: Boolean(enabled && clientId),
      staleTime: TWO_MINUTES,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  );

  useEffect(() => {
    if (error) toastError(error);
  }, [error]);

  if (!clientId) return null;

  return (
    <Paper className={classes.root} square variant="outlined">
      <Box className={classes.heading}>
        <HistoryIcon fontSize="small" />
        <Box className={classes.headingText}>
          <Typography variant="subtitle1">Histórico de OS</Typography>
          <Typography variant="caption" color="textSecondary">
            10 mais recentes deste cliente
          </Typography>
        </Box>
        {isLoading && <CircularProgress size={16} />}
      </Box>

      {!isLoading && !orders.length && (
        <Typography className={classes.empty} variant="body2">
          Nenhuma OS encontrada para este cliente.
        </Typography>
      )}

      {orders.map(order => (
        <Paper
          key={order.id}
          className={classes.card}
          variant="outlined"
          onClick={() => history.push(`/ordens-servico/${order.id}`)}
        >
          <Box className={classes.content}>
            <Box className={classes.title}>
              <Typography variant="subtitle2">OS #{order.id}</Typography>
              {order.status?.nome && (
                <Chip
                  label={order.status.nome}
                  size="small"
                  color={order.data_fechada ? "default" : "primary"}
                  variant="outlined"
                />
              )}
            </Box>
            <Typography
              className={classes.ellipsis}
              variant="caption"
              display="block"
            >
              {order.descricao_curta || "Sem descrição curta"}
            </Typography>
            <Typography
              className={classes.ellipsis}
              variant="caption"
              color="textSecondary"
              display="block"
            >
              {order.tecnico?.nome || "Sem técnico"} ·{" "}
              {formatDate(order.data_alterada || order.data_hora)}
            </Typography>
          </Box>
          <Tooltip title="Abrir OS">
            <IconButton size="small">
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        </Paper>
      ))}
    </Paper>
  );
};

export default ContactOsHistory;
